import { useMemo } from "react";
import { useLocation } from "@tanstack/react-router";
import moment from "moment";
import { useDebounce } from "@reactuses/core";
import { useTranslation } from "react-i18next";
import { useBlockWindowQuery } from "../../../components/table/useBlockWindowQuery";
import { useServerTable } from "../../../components/table/tableFeatures";
import { MdRotateLeft } from "react-icons/md";
import { css } from "../../../../styled-system/css";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../components/fields/base/TextField";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { resetTable } from "../../../components/table/utils";
import { useStandardEntityMetrics } from "../../merchandising/components/tables/metricUtils";
import TitleLink from "../../../components/core/basic/TitleLink";
import { Button } from "../../../components/core/basic/Button";
import { RT8FilterWidget } from "../../merchandising/components/tables/FilterWidget";
import getMetricSpec, { getFormatter } from "../../merchandising/utils/metrics";
import { GOAL_KPI_LOOKUP } from "../utils/constants";
import { snakeCaseToSentenceCase, toTitleCase } from "../../../utils/formatText";
import { useGetDSPOrdersPaginated } from "../hooks/useDSPOrdersPaginatedQuery";
import { getFlightDateRange } from "../utils/flights";
import { useDSPOrderStateSelectOptions, useDSPOrderStatusSelectOptions } from "../utils/dspLifecycleFilters";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import {
  type ManagedTableState,
  type ManagedTableStateSetters,
} from "../../../components/table/usePersistedTableState";
import type { DSPOrder } from "../../../zodios/apiDefinitions/api/dsp/orderDatasets";
import { useAppSelector } from "../../../redux/hooks";

const columnHelper = createColumnBuilder<DSPOrder>();

type Props = {
  dspAdvertiser?: {
    id: string;
  };
  state: ManagedTableState;
  setters: ManagedTableStateSetters;
  initialState: ManagedTableState;
};

const DSPOrdersTable = ({ dspAdvertiser, state, setters, initialState }: Props) => {
  const { t } = useTranslation(["dsp", "common"]);
  const dateFmt = useDateFormatter("medium");
  const marketPlace = useAppSelector((reduxState) => reduxState.marketPlace);
  const standardMetricIds = useStandardEntityMetrics("dspCampaign");
  const orderStatusSelectOptions = useDSPOrderStatusSelectOptions();
  const orderStateSelectOptions = useDSPOrderStateSelectOptions();

  const searchStr = useLocation({ select: (location) => location.searchStr });
  const queryString = useMemo(() => new URLSearchParams(searchStr).toString(), [searchStr]);

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("name", {
        id: "name",
        header: t(($) => $.dsp["Order"]),
        minSize: 250,
        size: 300,
        maxSize: 500,
        cell: ({ row, getValue }) => {
          const campaignId = row.original.campaign_id;
          let campaignDetailsURL = `/user/dsp/${dspAdvertiser?.id}/orders/${campaignId}`;

          if (queryString) {
            campaignDetailsURL = `${campaignDetailsURL}?${queryString}`;
          }

          return <TitleLink to={campaignDetailsURL} title={getValue()} />;
        },
        meta: {
          columnSelectorOptions: {
            label: t(($) => $.dsp["Order"]),
          },
        },
        enableColumnFilter: false,
      }),
      columnHelper.accessor("delivery_status", {
        id: "delivery_status",
        header: t(($) => $.dsp["Delivery Status"]),
        cell: ({ getValue }) => {
          const value = getValue();
          return toTitleCase(snakeCaseToSentenceCase(typeof value === "string" ? value : ""));
        },
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
        size: 200,
      }),
      columnHelper.accessor("state", {
        id: "state",
        header: t(($) => $.dsp["State"]),
        cell: ({ getValue }) => {
          const value = getValue();
          return toTitleCase(snakeCaseToSentenceCase(typeof value === "string" ? value : ""));
        },
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
        size: 140,
      }),
      columnHelper.accessor("flight_start_date", {
        id: "flight_start_date",
        header: t(($) => $.dsp["Start Date"]),
        cell: ({ row }) => {
          const { startDate } = getFlightDateRange(row.original.flights);
          return startDate ? dateFmt.format(moment(startDate).toDate()) : "-";
        },
        meta: {
          columnFilterOptions: {
            format: "date",
          },
        },
        size: 170,
      }),
      columnHelper.accessor("flight_end_date", {
        id: "flight_end_date",
        header: t(($) => $.dsp["End Date"]),
        cell: ({ row }) => {
          const { endDate } = getFlightDateRange(row.original.flights);
          return endDate ? dateFmt.format(moment(endDate).toDate()) : "-";
        },
        meta: {
          columnFilterOptions: {
            format: "date",
          },
        },
        size: 170,
      }),
      columnHelper.display({
        id: "goal",
        header: t(($) => $.dsp["Goal"]),
        cell: ({ row }) => {
          const campaign = row.original;
          const showGoalCompare = campaign.state === "ACTIVE" && campaign.delivery_status === "DELIVERING";

          if (!campaign.optimization) {
            return null;
          }

          const goalSpec = GOAL_KPI_LOOKUP[campaign.optimization.goalSetting.kpi];

          if (!goalSpec) {
            return null;
          }

          const rawGoalTarget = campaign.optimization.goalSetting.targetKpi ?? 0;

          const goalTarget = goalSpec.format === "percent" ? rawGoalTarget / 100 : rawGoalTarget;
          const metricSpec = getMetricSpec(goalSpec.accessor);
          const formatter = getFormatter(marketPlace, metricSpec.format, metricSpec);

          const metricValueRaw =
            goalSpec.accessor in campaign ? campaign[goalSpec.accessor as keyof typeof campaign] : null;
          const metricValue = typeof metricValueRaw === "number" ? metricValueRaw : 0;

          const onTarget = goalSpec.lower ? metricValue < goalTarget : metricValue > goalTarget;

          return (
            <span>
              <small>
                {t(($) => $.dsp["Actual {label}: {value}"], {
                  label: t(goalSpec.label),
                  value: showGoalCompare ? formatter(metricValue) : "-",
                })}
              </small>
              <br />
              <small>
                {showGoalCompare ?
                  <>
                    {onTarget ?
                      <span className={css({ color: "trellisPalette.12", fontWeight: "bold" })}>
                        {t(($) => $.dsp["On Target"])}
                      </span>
                    : <span className={css({ color: "trellisPalette.17", fontWeight: "bold" })}>
                        {t(($) => $.dsp["Off Target"])}
                      </span>
                    }
                  </>
                : null}{" "}
                {campaign.optimization.goalSetting.targetKpi != null ?
                  <span>{t(($) => $.dsp["(vs. Goal: {value})"], { value: formatter(goalTarget) })}</span>
                : <span className={css({ color: "trellisPalette.16", fontWeight: "bold" })}>
                    {t(($) => $.dsp["Pacing Not Applicable"])}
                  </span>
                }
              </small>
            </span>
          );
        },
        size: 240,
        enableColumnFilter: false,
      }),
      ...standardMetricIds.map((id) => columnHelper.metric(id)),
      columnHelper.accessor("creation_date_time", {
        id: "creation_date_time",
        header: t(($) => $.dsp["Created On"]),
        cell: ({ getValue }) => {
          const value = getValue();
          return value ? dateFmt.format(moment(value).toDate()) : "-";
        },
        meta: {
          columnFilterOptions: {
            format: "date",
          },
        },
        size: 170,
      }),
    ];
  }, [dateFmt, dspAdvertiser?.id, marketPlace, queryString, standardMetricIds, t]);

  const debouncedSearch = useDebounce(state.globalFilter, 500);

  const getQueryOptions = useGetDSPOrdersPaginated();
  const dspCampaignsQuery = useBlockWindowQuery(
    getQueryOptions,
    {
      pagination: state.pagination,
      sorting: state.sorting,
      filters: state.columnFilters,
      search: debouncedSearch,
    },
    { enabled: !!dspAdvertiser?.id },
  );

  const dspCampaignTableData = useMemo<DSPOrder[]>(() => {
    return (
      dspCampaignsQuery.data?.data.map((row) => {
        const createdOn = moment(row.creation_date_time).format();

        return {
          ...row,
          creation_date_time: createdOn,
        };
      }) ?? []
    );
  }, [dspCampaignsQuery.data]);

  const table = useServerTable({
    data: dspCampaignTableData,
    columns,
    initialState,
    state,
    ...setters,
    rowCount: dspCampaignsQuery.data?.count ?? undefined,
    meta: {
      showSkeleton: dspCampaignsQuery.isFetching && (!dspCampaignsQuery.data || dspCampaignsQuery.isPlaceholderData),
      columnFilterSelectOptions: {
        delivery_status: orderStatusSelectOptions,
        state: orderStateSelectOptions,
      },
    },
  });

  return (
    <>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={t(($) => $.common.actions.Search)}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table table={table} className={css({ minWidth: "100%" })} />
    </>
  );
};

export default DSPOrdersTable;
